import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useGameState } from '@/hooks/useGameState';
import { useGameSounds } from '@/hooks/useGameSounds';
import { VoiceType, NarrativeTone, GameRules, DEFAULT_GAME_RULES } from '@/types/game';
import { LandingScreen } from './LandingScreen';
import { SoundControl } from './SoundControl';

// Lazy load components that aren't needed on initial render
const PlayerSetup = lazy(() => import('./PlayerSetup').then(m => ({ default: m.PlayerSetup })));
const NarrationScreen = lazy(() => import('./NarrationScreen').then(m => ({ default: m.NarrationScreen })));
const RoleReveal = lazy(() => import('./RoleReveal').then(m => ({ default: m.RoleReveal })));
const NightPhase = lazy(() => import('./NightPhase').then(m => ({ default: m.NightPhase })));
const DayPhase = lazy(() => import('./DayPhase').then(m => ({ default: m.DayPhase })));
const VotingPhase = lazy(() => import('./VotingPhase').then(m => ({ default: m.VotingPhase })));
const EliminationScreen = lazy(() => import('./EliminationScreen').then(m => ({ default: m.EliminationScreen })));
const GameEnd = lazy(() => import('./GameEnd').then(m => ({ default: m.GameEnd })));
const HunterRevenge = lazy(() => import('./HunterRevenge').then(m => ({ default: m.HunterRevenge })));
const PhaseTransition = lazy(() => import('./PhaseTransition').then(m => ({ default: m.PhaseTransition })));

import { useNarrativeTexts, NARRATIVE_TONE_TEXTS } from '@/hooks/useNarrativeTexts';

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-foreground/60">Chargement...</div>
  </div>
);

type Screen = 'landing' | 'setup' | 'intro-narration' | 'role-reveal' | 'game';

export const GameController = () => {
  const [screen, setScreen] = useState<Screen>('landing');
  const [roleRevealIndex, setRoleRevealIndex] = useState(0);
  const [gameConfig, setGameConfig] = useState<{ playerCount: number; voiceType: VoiceType; narrativeTone: NarrativeTone; rules: GameRules } | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [showTransition, setShowTransition] = useState<'night' | 'day' | null>(null);
  
  const {
    gameState,
    createGame,
    setPhase,
    startNight,
    startDay,
    werewolfVote,
    seerReveal,
    witchHeal,
    witchKill,
    cupidChooseLovers,
    hunterRevenge,
    castVote,
    executeVote,
    resetGame,
  } = useGameState();

  const { playAmbient, stopAmbient, playEffect, toggleMute } = useGameSounds();

  // Handle ambient sounds based on game phase
  useEffect(() => {
    if (!gameState || isMuted) {
      stopAmbient();
      return;
    }

    if (gameState.phase === 'night') {
      playAmbient('night');
      playEffect('wolfHowl');
    } else if (gameState.phase === 'day' || gameState.phase === 'voting') {
      playAmbient('day');
    } else if (gameState.phase === 'end') {
      stopAmbient();
      if (gameState.winner === 'villagers') {
        playEffect('victory');
      } else {
        playEffect('defeat');
      }
    }
  }, [gameState?.phase, gameState?.winner, isMuted, playAmbient, stopAmbient, playEffect]);

  const handleToggleMute = () => {
    setIsMuted(!isMuted);
    toggleMute();
  };

  const handleStartSetup = () => {
    setScreen('setup');
  };

  const handleConfigComplete = (playerNames: string[], voiceType: VoiceType, narrativeTone: NarrativeTone, rules: GameRules) => {
    setGameConfig({ playerCount: playerNames.length, voiceType, narrativeTone, rules });
    createGame(playerNames, voiceType, narrativeTone);
    setScreen('intro-narration');
  };

  const handleIntroNarrationComplete = () => {
    setScreen('role-reveal');
    setRoleRevealIndex(0);
  };

  const handleRoleRevealNext = () => {
    setRoleRevealIndex(prev => prev + 1);
  };

  const handleRoleRevealComplete = () => {
    setShowTransition('night');
  };

  const handleTransitionComplete = () => {
    if (showTransition === 'night') {
      startNight();
      setScreen('game');
    } else if (showTransition === 'day') {
      startDay();
    }
    setShowTransition(null);
  };

  const handleNightComplete = () => {
    setShowTransition('day');
  };

  const handleStartVoting = () => {
    setPhase('voting');
  };

  const handleVote = useCallback((targetId: string) => {
    // In single-phone mode, we just record the collective vote
    if (gameState) {
      gameState.players.filter(p => p.isAlive).forEach(player => {
        castVote(player.id, targetId);
      });
    }
  }, [gameState, castVote]);

  const handleVoteComplete = () => {
    executeVote();
  };

  const handleEliminationContinue = () => {
    if (gameState?.winner) {
      setPhase('end');
    } else {
      setShowTransition('night');
    }
  };

  const handlePlayAgain = () => {
    resetGame();
    setScreen('landing');
    setRoleRevealIndex(0);
    setGameConfig(null);
    setShowTransition(null);
  };

  // Phase transition overlay
  if (showTransition) {
    return (
      <>
        <SoundControl isMuted={isMuted} onToggle={handleToggleMute} />
        <Suspense fallback={<LoadingFallback />}>
          <PhaseTransition to={showTransition} onComplete={handleTransitionComplete} />
        </Suspense>
      </>
    );
  }
  // Landing screen
  if (screen === 'landing') {
    return (
      <>
        <SoundControl isMuted={isMuted} onToggle={handleToggleMute} />
        <LandingScreen onStart={handleStartSetup} />
      </>
    );
  }
  if (screen === 'setup') {
    return (
      <>
        <SoundControl isMuted={isMuted} onToggle={handleToggleMute} />
        <Suspense fallback={<LoadingFallback />}>
          <PlayerSetup onStart={handleConfigComplete} />
        </Suspense>
      </>
    );
  }

  // Intro narration
  if (screen === 'intro-narration') {
    const introText = gameConfig ? NARRATIVE_TONE_TEXTS[gameConfig.narrativeTone].gameIntro() : NARRATIVE_TONE_TEXTS.taquin.gameIntro();
    
    return (
      <>
        <SoundControl isMuted={isMuted} onToggle={handleToggleMute} />
        <Suspense fallback={<LoadingFallback />}>
          <NarrationScreen 
            text={introText}
            onContinue={handleIntroNarrationComplete}
            autoSpeak={!isMuted}
            voiceType={gameConfig?.voiceType}
          />
        </Suspense>
      </>
    );
  }

  // Role reveal
  if (screen === 'role-reveal' && gameState) {
    return (
      <>
        <SoundControl isMuted={isMuted} onToggle={handleToggleMute} />
        <Suspense fallback={<LoadingFallback />}>
          <RoleReveal
            players={gameState.players}
            currentIndex={roleRevealIndex}
            onNext={handleRoleRevealNext}
            onComplete={handleRoleRevealComplete}
            narrativeTone={gameState.narrativeTone}
            voiceType={gameConfig?.voiceType || gameState.voiceType}
          />
        </Suspense>
      </>
    );
  }

  // Game screens
  if (!gameState) return null;

  const renderWithSoundControl = (component: React.ReactNode) => (
    <>
      <SoundControl isMuted={isMuted} onToggle={handleToggleMute} />
      <Suspense fallback={<LoadingFallback />}>
        {component}
      </Suspense>
    </>
  );

  const voiceType = gameConfig?.voiceType || gameState?.voiceType;
  const narrativeTone = gameConfig?.narrativeTone || gameState?.narrativeTone || 'taquin';

  switch (gameState.phase) {
    case 'night':
      return renderWithSoundControl(
        <NightPhase
          players={gameState.players}
          turnNumber={gameState.currentTurn}
          loversChosen={gameState.loversChosen}
          witchPotions={gameState.witchPotions}
          onWerewolfVote={werewolfVote}
          onSeerReveal={seerReveal}
          onWitchHeal={witchHeal}
          onWitchKill={witchKill}
          onCupidChooseLovers={cupidChooseLovers}
          onComplete={handleNightComplete}
          werewolfTarget={gameState.nightAction?.werewolfTarget}
          voiceType={voiceType}
          narrativeTone={narrativeTone}
        />
      );

    case 'day':
      return renderWithSoundControl(
        <DayPhase
          players={gameState.players}
          eliminatedId={gameState.eliminatedTonight}
          turnNumber={gameState.currentTurn}
          onStartVoting={handleStartVoting}
          voiceType={voiceType}
          narrativeTone={narrativeTone}
        />
      );

    case 'voting':
      return renderWithSoundControl(
        <VotingPhase
          players={gameState.players}
          onVote={handleVote}
          onComplete={handleVoteComplete}
          narrativeTone={narrativeTone}
        />
      );

    case 'elimination':
      return renderWithSoundControl(
        <EliminationScreen
          players={gameState.players}
          eliminatedId={gameState.eliminatedTonight}
          onContinue={handleEliminationContinue}
          voiceType={voiceType}
          narrativeTone={narrativeTone}
        />
      );

    case 'hunter-revenge':
      return renderWithSoundControl(
        <HunterRevenge
          players={gameState.players}
          hunterId={gameState.eliminatedTonight!}
          onRevenge={hunterRevenge}
          narrativeTone={narrativeTone}
        />
      );

    case 'end':
      return renderWithSoundControl(
        <GameEnd
          players={gameState.players}
          winner={gameState.winner!}
          nightEvents={gameState.nightEvents}
          onPlayAgain={handlePlayAgain}
          voiceType={voiceType}
          narrativeTone={narrativeTone}
        />
      );

    default:
      return <LandingScreen onStart={handleStartSetup} />;
  }
};
